import type { AxiosInstance } from 'axios';
import type { User, Group } from './types';

// Dummy data
const mockUser: User = {
  id: 'user_123',
  email: 'teacher@example.com',
  name: 'Anna Teacher',
  firstName: 'Anna',
  lastName: 'Teacher',
  userRole: 'teacher',
  avatar: '',
  createdAt: new Date().toISOString(),
};

const mockStudents: User[] = [
  {
    id: 'stu_1',
    email: 'john@student.com',
    name: 'John Smith',
    userRole: 'student',
  },
  {
    id: 'stu_2',
    email: 'maria@student.com',
    name: 'Maria Garcia',
    userRole: 'student',
  },
];

let mockGroups: Group[] = [
  {
    id: 'group_1',
    name: 'Advanced Speaking (B2)',
    invitationCode: 'SPK-2024',
    type: 'active',
    teacherId: 'user_123',
    ownerId: 'user_123',
    studentsIds: ['stu_1', 'stu_2'],
    users: mockStudents,
    createdAt: new Date().toISOString(),
  },
  {
    id: 'group_2',
    name: 'IELTS Prep Weekend',
    invitationCode: 'IELTS-99',
    type: 'active',
    teacherId: 'user_123',
    ownerId: 'user_123',
    studentsIds: [],
    users: [],
    createdAt: new Date().toISOString(),
  },
];

const mockExercises: any[] = [
  { id: 'ex1', name: 'Present Perfect — Gap Filling', exerciseType: 'gaps', description: 'Fill in the gaps with the correct form of the verb in Present Perfect.', task: 'Insert the missing words.', access: 'private', weight: 1, creatorId: 'user_123', timestamp: Date.now() - 100000, createdAt: new Date(Date.now() - 100000).toISOString() },
  { id: 'ex2', name: 'Business Vocabulary — Matching', exerciseType: 'matching', description: 'Match the business terms with their definitions.', task: 'Match items from the left column with the right column.', access: 'public', weight: 1, creatorId: 'user_123', timestamp: Date.now() - 200000, createdAt: new Date(Date.now() - 200000).toISOString() },
  { id: 'ex3', name: 'TED Talk Comprehension', exerciseType: 'multiple_choice', description: 'Watch the TED talk and answer the questions.', task: 'Choose the correct answer for each question.', access: 'private', weight: 2, creatorId: 'user_123', timestamp: Date.now() - 300000, createdAt: new Date(Date.now() - 300000).toISOString() },
  { id: 'ex4', name: 'Conditional Sentences — True/False', exerciseType: 'true_false', description: 'Determine whether the statements are true or false.', task: 'Read the statements and choose TRUE, FALSE or NOT STATED.', access: 'private', weight: 1, creatorId: 'user_123', timestamp: Date.now() - 400000, createdAt: new Date(Date.now() - 400000).toISOString() },
  { id: 'ex5', name: 'Pronunciation Drill — Recording', exerciseType: 'recording', description: 'Listen and repeat the phrases.', task: 'Click "start recording" and repeat verbatim.', access: 'public', weight: 1, creatorId: 'user_123', timestamp: Date.now() - 500000, createdAt: new Date(Date.now() - 500000).toISOString() },
  { id: 'ex6', name: 'Word Order — Restore', exerciseType: 'order', description: 'Restore the correct word order.', task: 'Put the words in the correct order.', access: 'private', weight: 1, creatorId: 'user_123', timestamp: Date.now() - 600000, createdAt: new Date(Date.now() - 600000).toISOString() },
];

/**
 * Configure axios-mock-adapter using native axios interceptors
 * (so we don't have to install another dependency just for mocking).
 */
export function setupMockApi(apiClient: AxiosInstance) {
  console.warn('⚠️ MOCK API ENABLED: Using custom adapter to bypass network entirely.');

  // Override the default adapter to bypass XMLHttpRequest / HTTP completely
  apiClient.defaults.adapter = async (config) => {
    const method = config.method?.toUpperCase();
    const url = config.url || '';

    // Helper to simulate network delay
    const delay = (ms = 300) => new Promise((resolve) => setTimeout(resolve, ms));
    await delay();

    const mockResponse = (data: any, status = 200) => {
      return {
        data,
        status,
        statusText: 'OK',
        headers: {},
        config,
      };
    };

    try {
      // ── Auth ──
      if (url.endsWith('/login') && method === 'POST') {
        const { email } = JSON.parse(config.data);
        const role = email.includes('student') ? 'student' : 'teacher';
        
        mockUser.email = email;
        mockUser.userRole = role;
        mockUser.name = role === 'teacher' ? 'Mock Teacher' : 'Mock Student';

        return mockResponse({ token: 'mock-jwt-token-777' });
      }

      if (url.endsWith('/users/create') && method === 'POST') {
        const payload = JSON.parse(config.data);
        mockUser.email = payload.email;
        mockUser.userRole = payload.userRole || 'student';
        mockUser.name = payload.name;
        return mockResponse({ token: 'mock-jwt-token-777' });
      }

      if (url.endsWith('/users/me') && method === 'GET') {
        return mockResponse(mockUser);
      }

      if (url.endsWith('/user/update') && method === 'POST') {
        const payload = JSON.parse(config.data);
        Object.assign(mockUser, payload);
        return mockResponse(mockUser);
      }

      if (url.endsWith('/change-my-password') && method === 'POST') {
        return mockResponse({ success: true });
      }

      // ── Groups ──
      if (url.endsWith('/v3/classes') && method === 'GET') {
        return mockResponse(mockGroups);
      }

      if (url.match(/\/classes\/([^/]+)\/with-students$/) && method === 'GET') {
        const id = url.split('/').slice(-2)[0];
        const group = mockGroups.find((g) => g.id === id);
        if (group) return mockResponse(group);
        throw { response: { status: 404, data: { error: { message: 'Group not found' } } } };
      }

      if (url.endsWith('/v3/classes') && method === 'POST') {
        const payload = JSON.parse(config.data);
        const newGroup: Group = {
          id: `group_${Date.now()}`,
          name: payload.name,
          invitationCode: Math.random().toString(36).substring(2, 8).toUpperCase(),
          ownerId: mockUser.id,
          users: [],
        };
        mockGroups = [newGroup, ...mockGroups];
        return mockResponse({ group: newGroup });
      }

      if (url.endsWith('/v3/classes') && method === 'PUT') {
        const payload = JSON.parse(config.data);
        mockGroups = mockGroups.map((g) => (g.id === payload.id ? { ...g, ...payload } : g));
        return mockResponse(mockGroups.find((g) => g.id === payload.id));
      }

      if (url.includes('/v3/class/') && method === 'DELETE') {
        const id = url.split('/').pop();
        mockGroups = mockGroups.filter((g) => g.id !== id);
        return mockResponse({ success: true });
      }

      if (url.endsWith('/add_to_class') && method === 'POST') {
        const { invitationCode } = JSON.parse(config.data);
        const group = mockGroups.find((g) => g.invitationCode === invitationCode);
        if (!group) return mockResponse({ error: { message: 'Неверный код' } });
        group.users = [...(group.users || []), mockUser];
        return mockResponse({ result: mockGroups });
      }

      if (url.endsWith('/remove_user_from_class') && method === 'POST') {
        const { classId, userId } = JSON.parse(config.data);
        const group = mockGroups.find((g) => g.id === classId);
        if (group && group.users) {
          group.users = group.users.filter((u) => u.id !== userId);
        }
        return mockResponse({ result: mockGroups });
      }

      // ── Materials ──
      let mockMaterials = [
        { id: 'mat1', title: 'English Grammar in Use', type: 'pdf', createdAt: new Date().toISOString() },
        { id: 'mat2', title: 'TED Talk: Body Language', type: 'video', createdAt: new Date().toISOString() },
        { id: 'mat3', title: 'Interactive Vocabulary Set', type: 'link', createdAt: new Date().toISOString() }
      ];
      
      let mockCollections: any[] = [];

      if (url.endsWith('/v2/materials') && method === 'GET') {
        return mockResponse({ result: mockMaterials });
      }

      if (url.endsWith('/v2/materials') && method === 'POST') {
        const payload = JSON.parse(config.data);
        const newMaterial = { id: `mat_${Date.now()}`, ...payload, createdAt: new Date().toISOString() };
        mockMaterials = [newMaterial, ...mockMaterials];
        return mockResponse({ result: newMaterial });
      }

      if (url.endsWith('/v2/collections') && method === 'GET') {
        return mockResponse({ result: mockCollections });
      }

      if (url.endsWith('/v2/collections') && method === 'POST') {
        const payload = JSON.parse(config.data);
        const newCollection = { id: `col_${Date.now()}`, ...payload, createdAt: new Date().toISOString() };
        mockCollections = [newCollection, ...mockCollections];
        return mockResponse({ result: newCollection });
      }

      // ── Exercises ──
      if (url.endsWith('/v3/all-exercises') && method === 'GET') {
        return mockResponse({ result: mockExercises });
      }

      if (url.includes('/v3/exercises') && method === 'GET') {
        const parts = url.split('/');
        const lastPart = parts[parts.length - 1] ?? '';
        if (lastPart !== 'exercises' && !lastPart.includes('?')) {
          const ex = mockExercises.find((e: any) => e.id === lastPart);
          return mockResponse({ result: ex || mockExercises[0] });
        }
        return mockResponse({ result: mockExercises });
      }

      if (url.endsWith('/v3/exercises') && method === 'POST') {
        const payload = JSON.parse(config.data);
        const newEx = { id: `ex_${Date.now()}`, ...payload, creatorId: mockUser.id, timestamp: Date.now(), createdAt: new Date().toISOString() };
        mockExercises.unshift(newEx);
        return mockResponse({ result: newEx });
      }

      if (url.endsWith('/v3/exercises') && method === 'PUT') {
        const payload = JSON.parse(config.data);
        const idx = mockExercises.findIndex((e: any) => e.id === payload.id);
        if (idx >= 0) { mockExercises[idx] = { ...mockExercises[idx], ...payload }; }
        return mockResponse({ result: mockExercises[idx] || payload });
      }

      if (url.includes('/v3/exercises/') && method === 'DELETE') {
        const id = url.split('/').pop();
        const idx = mockExercises.findIndex((e: any) => e.id === id);
        if (idx >= 0) mockExercises.splice(idx, 1);
        return mockResponse({ result: { success: true } });
      }

      console.warn(`Mock API: Unhandled endpoint ${method} ${url}`);
      throw { response: { status: 404, data: { error: { message: 'Route not mocked' } } } };

    } catch (error) {
      // Re-throw in Axios format
      throw error;
    }
  };
}
